import { OPS_DOCUMENT_MAX_BYTES } from '@utazzo/shared';
import { cn, UzBadge, UzButton, UzInput, UzSelect, UzSpinner } from '@utazzo/ui';
import {
  ExternalLink,
  FileImage,
  FileText,
  Lock,
  Send,
  Trash2,
  Upload,
  UserRound,
} from 'lucide-react';
import { useRef, useState } from 'react';

import { fileToBase64 } from '../file-base64';

import type {
  OpsCaseDocument,
  OpsDocumentAudience,
  OpsDocumentKind,
  OpsDocumentUploadInput,
} from '@utazzo/shared';

/**
 * Every file on a converted lead in one place - Accounts' payment proofs,
 * the tickets / vouchers ops attached while issuing, hand uploads - split
 * into what goes to the customer and what stays internal.
 *
 * Used read-only on the lead page (seller forwards the customer files) and
 * with upload / remove inside the Bookings drawer.
 */

export const DOC_ACCEPT =
  'image/png,image/jpeg,image/webp,application/pdf,.png,.jpg,.jpeg,.webp,.pdf';

type UploadKind = Exclude<OpsDocumentKind, 'PAYMENT_PROOF'>;

export const KIND_LABEL: Record<OpsDocumentKind, string> = {
  PAYMENT_PROOF: 'Payment proof',
  TICKET: 'Ticket',
  VOUCHER: 'Voucher',
  INVOICE: 'Invoice',
  OTHER: 'Other',
};

export const KIND_OPTIONS: { value: UploadKind; label: string }[] = [
  { value: 'TICKET', label: 'Ticket / e-ticket' },
  { value: 'VOUCHER', label: 'Voucher' },
  { value: 'INVOICE', label: 'Supplier invoice' },
  { value: 'OTHER', label: 'Other' },
];

export const AUDIENCE_OPTIONS: { value: OpsDocumentAudience; label: string }[] = [
  { value: 'CUSTOMER', label: 'For customer' },
  { value: 'INTERNAL', label: 'Internal only' },
];

const fmtSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const fmtWhen = (d: Date): string =>
  d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export const DocRow = ({
  doc,
  onOpen,
  onDelete,
  busy,
}: {
  doc: OpsCaseDocument;
  onOpen: () => void;
  onDelete?: () => void;
  busy?: boolean;
}): React.JSX.Element => {
  const Icon = doc.mimeType.startsWith('image/') ? FileImage : FileText;
  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-zinc-200 bg-white px-3 py-2">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-600">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 text-sm">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate font-medium text-zinc-900">{doc.fileName}</span>
          <UzBadge tone={doc.kind === 'PAYMENT_PROOF' ? 'warning' : 'secondary'}>
            {KIND_LABEL[doc.kind]}
          </UzBadge>
        </div>
        <div className="truncate text-xs text-zinc-500">
          {doc.componentTitle ? `${doc.componentTitle} · ` : ''}
          {fmtSize(doc.sizeBytes)} · {doc.uploadedByName} · {fmtWhen(doc.createdAt)}
          {doc.note ? ` · ${doc.note}` : ''}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <UzButton size="sm" variant="ghost" onClick={onOpen} disabled={busy} title="Open">
          <ExternalLink className="h-3.5 w-3.5" />
        </UzButton>
        {onDelete && doc.canDelete ? (
          <UzButton
            size="sm"
            variant="ghost"
            onClick={onDelete}
            disabled={busy}
            title="Remove"
            className="text-rose-600 hover:text-rose-700"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </UzButton>
        ) : null}
      </div>
    </li>
  );
};

const Group = ({
  title,
  icon,
  hint,
  docs,
  onOpen,
  onDelete,
  busy,
}: {
  title: string;
  icon: React.ReactNode;
  hint: string;
  docs: OpsCaseDocument[];
  onOpen: (doc: OpsCaseDocument) => void;
  onDelete?: (doc: OpsCaseDocument) => void;
  busy?: boolean;
}): React.JSX.Element => (
  <div>
    <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.06em] text-zinc-500 uppercase">
      {icon}
      {title}
      <span className="font-medium text-zinc-400 normal-case">· {docs.length}</span>
    </div>
    {docs.length === 0 ? (
      <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-2 text-xs text-zinc-500">
        {hint}
      </p>
    ) : (
      <ul className="space-y-1.5">
        {docs.map((d) => (
          <DocRow
            key={d.id}
            doc={d}
            onOpen={() => onOpen(d)}
            onDelete={onDelete ? () => onDelete(d) : undefined}
            busy={busy}
          />
        ))}
      </ul>
    )}
  </div>
);

/** One audience's files with its heading (the tabbed Documents block shows one per tab). */
export const OpsDocumentsGroup = Group;

/** Two groups: for the customer, internal. */
export const OpsDocumentsList = ({
  docs,
  onOpen,
  onDelete,
  busy,
}: {
  docs: OpsCaseDocument[];
  onOpen: (doc: OpsCaseDocument) => void;
  onDelete?: (doc: OpsCaseDocument) => void;
  busy?: boolean;
}): React.JSX.Element => (
  <div className="space-y-3">
    <Group
      title="For the customer"
      icon={<Send className="h-3 w-3" />}
      hint="Tickets and vouchers to send to the customer show up here once ops issues the bookings."
      docs={docs.filter((d) => d.audience === 'CUSTOMER')}
      onOpen={onOpen}
      onDelete={onDelete}
      busy={busy}
    />
    <Group
      title="Internal"
      icon={<Lock className="h-3 w-3" />}
      hint="Accounts' payment proofs and supplier invoices stay here."
      docs={docs.filter((d) => d.audience === 'INTERNAL')}
      onOpen={onOpen}
      onDelete={onDelete}
      busy={busy}
    />
  </div>
);

/** Upload one file with its kind, audience and (optionally) the item it is about. */
export const OpsDocumentUploadForm = ({
  items,
  pending,
  onSubmit,
  onCancel,
}: {
  items: { componentId: string; title: string }[];
  pending: boolean;
  onSubmit: (input: OpsDocumentUploadInput) => void;
  onCancel: () => void;
}): React.JSX.Element => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<UploadKind>('TICKET');
  const [audience, setAudience] = useState<OpsDocumentAudience>('CUSTOMER');
  const [componentId, setComponentId] = useState<string>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (!file) return setError('Choose a file');
    if (file.size > OPS_DOCUMENT_MAX_BYTES) return setError('File is too large (max 8MB)');
    const contentBase64 = await fileToBase64(file);
    onSubmit({
      fileName: file.name,
      contentBase64,
      kind,
      audience,
      ...(componentId ? { componentId } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border-2 border-dashed bg-white px-3 py-3 text-left text-sm',
          file ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-300 hover:bg-zinc-50',
        )}
      >
        <Upload className="h-4 w-4 text-zinc-500" />
        <span className="min-w-0 flex-1 truncate">
          {file ? file.name : 'Choose a PNG, JPG, WebP or PDF (max 8MB)'}
        </span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept={DOC_ACCEPT}
        className="sr-only"
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          setError(null);
          e.target.value = '';
        }}
      />
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <UzSelect<UploadKind>
          label="Type"
          value={kind}
          onValueChange={setKind}
          options={KIND_OPTIONS}
        />
        <UzSelect<OpsDocumentAudience>
          label="Who is it for"
          value={audience}
          onValueChange={setAudience}
          options={AUDIENCE_OPTIONS}
        />
      </div>
      {items.length ? (
        <UzSelect<string>
          containerClassName="mt-2"
          label="Booking item (optional)"
          value={componentId}
          onValueChange={setComponentId}
          options={[
            { value: '', label: 'Whole trip' },
            ...items.map((i) => ({ value: i.componentId, label: i.title })),
          ]}
        />
      ) : null}
      <UzInput
        containerClassName="mt-2"
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      <div className="mt-3 flex justify-end gap-2">
        <UzButton size="sm" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" loading={pending} onClick={() => void submit()}>
          <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload
        </UzButton>
      </div>
    </div>
  );
};

/** Small chip for the drawer header: who the file is for. */
export const AudienceChip = ({ audience }: { audience: OpsDocumentAudience }): React.JSX.Element =>
  audience === 'CUSTOMER' ? (
    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
      <UserRound className="h-3 w-3" /> customer
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
      <Lock className="h-3 w-3" /> internal
    </span>
  );

export const DocumentsLoading = (): React.JSX.Element => (
  <div className="flex items-center gap-2 py-3 text-sm text-zinc-500">
    <UzSpinner size="sm" /> Loading documents…
  </div>
);
